// Likelihood table management page (FRMW-002).
// Versioned levels with draft → edit → activate lifecycle.
// Level count is hard-locked by scoring grid target_likelihood_level_count.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  likelihoodApi,
  scoringGridApi,
  deriveVersionStatus,
  type LevelInput,
  type ActivationResponse,
} from '@/lib/api/frameworkConfig'
import { useEntitlements } from '@/lib/hooks/use-entitlements'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { useToast } from '@/lib/hooks/use-toast'
import { Pencil, Play, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react'

import {
  ActiveLevelDisplay,
  VersionList,
  LevelEditorDialog,
  EditDraftLoader,
  ActivateConfirmDialog,
  emptyLevels,
  type PageMode,
} from '@/components/features/versioned-levels'
import type { VersionedLevelLabels } from '@/components/features/versioned-levels/types'

export function LikelihoodPage() {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const lkApi = likelihoodApi(api)
  const sgApi = scoringGridApi(api)
  const { data: entitlements } = useEntitlements()
  const hasConfirmationGates = entitlements?.confirmation_gates === true

  const [mode, setMode] = useState<PageMode>({ kind: 'view' })
  const [showVersions, setShowVersions] = useState(false)

  // Shared labels for versioned-level components
  const labels: VersionedLevelLabels = {
    createDraft: t('likelihood.createDraft'),
    editDraft: t('likelihood.editDraft'),
    activate: t('likelihood.activate'),
    activateConfirm: hasConfirmationGates
      ? t('likelihood.activateConfirmWithEmail')
      : t('likelihood.activateConfirm'),
    versionLabel: (n) => t('likelihood.versionLabel', { number: n }),
    statusActive: t('likelihood.statusActive'),
    statusDraft: t('likelihood.statusDraft'),
    statusRetired: t('likelihood.statusRetired'),
    activeLevels: t('likelihood.activeLevels'),
    noActiveVersion: t('likelihood.noActiveVersion'),
    noVersions: t('likelihood.noVersions'),
    levelCountInfo: (count) => t('likelihood.levelCountInfo', { count }),
    levelLabel: t('likelihood.levelLabel'),
    levelDescription: t('likelihood.levelDescription'),
    versions: t('likelihood.versions'),
  }

  // Fetch scoring grid (determines level count)
  const scoringGridQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.scoringGrid(slug),
    queryFn: () => sgApi.get(),
    retry: (count, error) => {
      if (error instanceof ApiRequestError && error.status === 404) return false
      return count < 3
    },
  })

  // Fetch active version
  const activeQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.likelihood(slug),
    queryFn: () => lkApi.getActive(),
    retry: (count, error) => {
      if (error instanceof ApiRequestError && error.status === 404) return false
      return count < 3
    },
  })

  // Fetch version list (always — needed to detect draft for header buttons)
  const versionsQuery = useQuery({
    queryKey: queryKeys.frameworkConfig.likelihoodVersions(slug),
    queryFn: () => lkApi.listVersions(),
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.likelihood(slug) })
    queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.likelihoodVersions(slug) })
  }

  // Create draft
  const createMutation = useMutation({
    mutationFn: (levels: LevelInput[]) => lkApi.createVersion(levels),
    onSuccess: () => {
      invalidateAll()
      setMode({ kind: 'view' })
      toast({ title: labels.createDraft + ' ✓' })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // Edit draft
  const editMutation = useMutation({
    mutationFn: ({ versionId, levels }: { versionId: string; levels: LevelInput[] }) =>
      lkApi.editVersion(versionId, levels),
    onSuccess: () => {
      invalidateAll()
      setMode({ kind: 'view' })
      toast({ title: t('likelihood.draftUpdated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // Activate
  const activateMutation = useMutation({
    mutationFn: (versionId: string): Promise<ActivationResponse> => lkApi.activate(versionId),
    onSuccess: (data) => {
      if (data.status === 'pending_confirmation') {
        setMode({ kind: 'view' })
        toast({
          title: t('likelihood.confirmationSent'),
          description: t('likelihood.checkEmail'),
        })
        return
      }
      invalidateAll()
      setMode({ kind: 'view' })
      toast({ title: t('likelihood.activated') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // Loading state
  if (scoringGridQuery.isLoading || activeQuery.isLoading) {
    return <TableSkeleton />
  }

  // Scoring grid error (non-404)
  if (
    scoringGridQuery.error &&
    !(scoringGridQuery.error instanceof ApiRequestError && scoringGridQuery.error.status === 404)
  ) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('scoringGrid.title') })}
        technicalDetail={
          scoringGridQuery.error instanceof ApiRequestError
            ? scoringGridQuery.error.message
            : scoringGridQuery.error.message
        }
        onRetry={() => scoringGridQuery.refetch()}
      />
    )
  }

  // No scoring grid configured
  if (!scoringGridQuery.data) {
    return (
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{t('likelihood.title')}</h1>
          <p className="text-muted-foreground">{t('likelihood.description')}</p>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center gap-3 py-8 text-muted-foreground">
            <AlertTriangle className="h-5 w-5" />
            <span>{t('likelihood.scoringGridRequired')}</span>
          </CardContent>
        </Card>
      </div>
    )
  }

  const levelCount = scoringGridQuery.data.target_likelihood_level_count

  // Active version error (non-404)
  if (
    activeQuery.error &&
    !(activeQuery.error instanceof ApiRequestError && activeQuery.error.status === 404)
  ) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('likelihood.title') })}
        technicalDetail={
          activeQuery.error instanceof ApiRequestError
            ? activeQuery.error.message
            : activeQuery.error.message
        }
        onRetry={() => activeQuery.refetch()}
      />
    )
  }

  const activeVersion = activeQuery.data
  const hasActive = !!activeVersion
  const versions = versionsQuery.data ?? []
  const draftVersion = versions.find((v) => deriveVersionStatus(v) === 'draft')

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('likelihood.title')}</h1>
          <p className="text-muted-foreground">
            {t('likelihood.description')} {labels.levelCountInfo(levelCount)}
          </p>
        </div>
        <div className="flex gap-2">
          {draftVersion ? (
            <>
              <Button
                variant="outline"
                onClick={() =>
                  setMode({
                    kind: 'edit',
                    versionId: draftVersion.id,
                    versionNumber: draftVersion.version_number,
                  })
                }
              >
                <Pencil className="mr-2 h-4 w-4" /> {labels.editDraft}
              </Button>
              <Button
                onClick={() =>
                  setMode({
                    kind: 'activate-confirm',
                    versionId: draftVersion.id,
                    versionNumber: draftVersion.version_number,
                  })
                }
              >
                <Play className="mr-2 h-4 w-4" /> {labels.activate}
              </Button>
            </>
          ) : (
            <Button onClick={() => setMode({ kind: 'create' })}>
              <Pencil className="mr-2 h-4 w-4" /> {labels.createDraft}
            </Button>
          )}
        </div>
      </div>

      {/* Active version display */}
      {hasActive ? (
        <ActiveLevelDisplay
          versionNumber={activeVersion.version_number}
          levels={activeVersion.levels}
          labels={labels}
        />
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {labels.noActiveVersion}
          </CardContent>
        </Card>
      )}

      {/* Version history toggle */}
      <Button variant="ghost" className="text-sm" onClick={() => setShowVersions(!showVersions)}>
        {showVersions ? (
          <ChevronUp className="mr-2 h-4 w-4" />
        ) : (
          <ChevronDown className="mr-2 h-4 w-4" />
        )}
        {labels.versions}
      </Button>

      {showVersions && (
        <VersionList
          versions={versions}
          isLoading={versionsQuery.isLoading}
          labels={labels}
          onEditDraft={(v) =>
            setMode({ kind: 'edit', versionId: v.id, versionNumber: v.version_number })
          }
          onActivate={(v) =>
            setMode({ kind: 'activate-confirm', versionId: v.id, versionNumber: v.version_number })
          }
        />
      )}

      {mode.kind === 'create' && (
        <LevelEditorDialog
          mode="create"
          levelCount={levelCount}
          initialLevels={
            activeVersion?.levels.map((l) => ({
              level_number: l.level_number,
              label: l.label,
              description: l.description ?? '',
            })) ?? emptyLevels(levelCount)
          }
          isPending={createMutation.isPending}
          labels={labels}
          saveLabel={t('common:save')}
          createLabel={t('common:create')}
          savingLabel={t('common:saving')}
          cancelLabel={t('common:cancel')}
          onSubmit={(levels) => createMutation.mutate(levels)}
          onCancel={() => setMode({ kind: 'view' })}
        />
      )}

      {mode.kind === 'edit' && (
        <EditDraftLoader
          versionId={mode.versionId}
          versionNumber={mode.versionNumber}
          levelCount={levelCount}
          isPending={editMutation.isPending}
          labels={labels}
          saveLabel={t('common:save')}
          cancelLabel={t('common:cancel')}
          savingLabel={t('common:saving')}
          queryKey={[...queryKeys.frameworkConfig.likelihoodVersions(slug), mode.versionId]}
          fetchVersion={(vid) => lkApi.getVersion(vid)}
          onSubmit={(levels) => editMutation.mutate({ versionId: mode.versionId, levels })}
          onCancel={() => setMode({ kind: 'view' })}
        />
      )}

      {mode.kind === 'activate-confirm' && (
        <ActivateConfirmDialog
          versionNumber={mode.versionNumber}
          isPending={activateMutation.isPending}
          labels={labels}
          cancelLabel={t('common:cancel')}
          onConfirm={() => activateMutation.mutate(mode.versionId)}
          onCancel={() => setMode({ kind: 'view' })}
        />
      )}
    </div>
  )
}
